import { type ClassValue, clsx } from "clsx";
import type { IntlShape } from "react-intl";
import { twMerge } from "tailwind-merge";
import { type ProductionUnit, getProductionUnit, isLocale } from "../..";

export function getCSSVar(property: string) {
	return (
		Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue(property), 10) || 0
	);
}

export function cn(...args: ClassValue[]) {
	return twMerge(clsx(args));
}

export function openInCurrentTab(url: string) {
	window.open(url, "_self")?.focus();
}

export function openInNewTab(url: string) {
	window.open(url, "_blank", "noopener,noreferrer")?.focus();
}

export function createUrlFromOrigin(href?: string) {
	const origin = window.location.origin;
	const sanitizedHref = href?.replace(/^\/+/, "");

	return sanitizedHref ? `${origin}/${sanitizedHref}` : origin;
}

export function createUrl(entries: Record<string, string | null>, href?: string) {
	if (!href?.startsWith("http")) {
		throw new Error("`href` must be a valid URL");
	}

	const urlObj = new URL(href ?? createUrlFromOrigin(window.location.href));
	const searchParams = new URLSearchParams(urlObj.search);

	const createQueryString = () => {
		const params = new URLSearchParams(Object.fromEntries(searchParams.entries()));

		for (const [name, value] of Object.entries(entries)) {
			if (value) {
				params.set(name, value);
			} else {
				params.delete(name);
			}
		}

		return params.toString();
	};

	const params = createQueryString();

	return urlObj.pathname + (params ? `?${params}` : "");
}

export function saveFile(resource: Blob | string, filename: string) {
	const link = document.createElement("a");
	link.style.display = "none";
	document.body.appendChild(link); // Firefox workaround, see #6594

	link.href = typeof resource === "string" ? resource : URL.createObjectURL(resource);
	link.download = filename;
	link.click();
}

export function normalizeFiles(input: unknown) {
	if (typeof window === "object" && input instanceof FileList) {
		return Array.from(input);
	}

	if (Array.isArray(input) && input.every((file) => file instanceof File)) {
		return input as File[];
	}

	return [];
}

export const formatUnit = (intl: IntlShape, unit?: ProductionUnit | string): string => {
	const formattedUnit = getProductionUnit(intl, unit);
	return isLocale({ current: intl.locale, pattern: "language", supported: ["de"] })
		? formattedUnit
		: formattedUnit.toLowerCase();
};

export function getStateProperty<T extends "string" | "number">(
	state: unknown,
	key: string,
	type: T,
) {
	if (typeof state === "object" && state !== null && key in state) {
		const value = (state as Record<string, unknown>)[key];

		if (
			(type === "string" && typeof value === "string") ||
			(type === "number" && typeof value === "number")
		) {
			return value as T extends "string" ? string : number;
		}
	}
	return null;
}
